import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LayoutList, Bell, Plus, Map as MapIcon, User } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE } from '@/utils/api';
import { useTableStates } from '@/components/tables/useTableStates';
import { STATE_META, urgencyRank, money, people } from '@/components/tables/tableStates';
import { FloorPlan } from '../floor/FloorPlan';
import { OrderTaker } from './OrderTaker';
import { fmtElapsed } from '../shared';

const sub = (u) => {
  if (u.state === 'waiter_call') return '¿Necesita algo?';
  if (u.state === 'order_ready') return `${u.ready_count || 1} pedido(s) listo(s)`;
  if (u.state === 'reserved') return u.reservation ? `${u.reservation.customer_name} · ${people(u.reservation.party_size)}` : 'Reservada';
  if (u.state === 'to_clean') return 'Esperando limpieza';
  if (!u.session_id) return 'Mesa libre';
  return people(u.guest_count);
};

const Row = ({ u, now, onTap }) => {
  const m = STATE_META[u.state] || STATE_META.free;
  return (
    <button onClick={() => onTap(u)} className="w-full flex items-center gap-3 px-4 py-3 border-b border-border text-left" data-testid={`waiter-row-${u.unit_id}`}>
      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: m.color }} />
      <span className="w-10 text-3xl font-bold tabular-nums">{u.name.replace(/^\D+/, '') || u.name}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>{m.label}</span>
        <span className="block text-sm truncate">{sub(u)}</span>
      </span>
      <span className="text-right text-sm">
        <span className="block font-semibold">{u.state === 'reserved' ? u.reservation?.time : u.session_id ? fmtElapsed(u.opened_at, now) : '-'}</span>
        {u.total ? <span className="block font-bold">{money(u.total)}</span> : null}
      </span>
    </button>
  );
};

export default function ModernWaiter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { units, decor, now, refresh } = useTableStates();
  const [mine, setMine] = useState(false);
  const [tab, setTab] = useState('list');
  const [picked, setPicked] = useState(null);
  const [ordering, setOrdering] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [pickNew, setPickNew] = useState(false);
  const list = useMemo(() => units.filter(u => !mine || u.waiter_id === user?.user_id)
    .sort((a, b) => urgencyRank(a.state) - urgencyRank(b.state) || (a.session_id ? 0 : 1) - (b.session_id ? 0 : 1)), [units, mine, user]);
  const openUnits = list.filter(u => u.session_id);
  const alerts = units.filter(u => u.state === 'waiter_call' || u.state === 'order_ready');
  const open = async (u) => {
    try { await axios.post(`${API_BASE}/sessions`, { unit_id: u.unit_id }); toast.success(`${u.name} abierta`); await refresh(); setPicked(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo abrir'); }
  };
  const clean = async (u) => { await axios.post(`${API_BASE}/tables/${u.unit_id}/clean`); toast.success('Mesa lista'); refresh(); setPicked(null); };
  const showMap = async () => { setTab('map'); if (!spaces.length) setSpaces((await axios.get(`${API_BASE}/spaces`)).data); };
  const shown = tab === 'alerts' ? alerts : list;
  return (
    <div className="pb-24 max-w-2xl mx-auto" data-testid="modern-waiter">
      <div className="flex items-center justify-between px-4 py-3">
        <h1 className="text-xl font-bold">{tab === 'alerts' ? 'Avisos' : 'Mis mesas'}</h1>
        <label className="flex items-center gap-2 text-sm">Solo mis mesas<Switch checked={mine} onCheckedChange={setMine} data-testid="waiter-only-mine" /></label>
      </div>
      {tab === 'map' ? (
        <div className="px-2"><FloorPlan units={[...units, ...decor]} spaces={spaces} spaceId="all" view="simple" now={now} onSelect={setPicked} /></div>
      ) : (
        <div className="m-card mx-2 overflow-hidden">{shown.map(u => <Row key={u.unit_id} u={u} now={now} onTap={setPicked} />)}</div>
      )}
      {picked && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setPicked(null)}>
          <div className="w-full bg-card rounded-t-2xl p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="waiter-sheet">
            <h2 className="text-2xl font-bold">{picked.name}</h2>
            <p className="text-sm">{STATE_META[picked.state]?.label} · {sub(picked)}</p>
            {!picked.session_id && picked.state !== 'to_clean' && <Button className="w-full h-12 bg-[#2563EB] text-white" onClick={() => open(picked)} data-testid="waiter-open-btn">Abrir mesa</Button>}
            {picked.session_id && <Button className="w-full h-12 bg-[#2563EB] text-white" onClick={() => { setOrdering(picked); setPicked(null); }} data-testid="waiter-order-btn">Tomar pedido</Button>}
            {picked.state === 'to_clean' && <Button className="w-full h-12" onClick={() => clean(picked)} data-testid="waiter-clean-btn">Marcar limpia</Button>}
            <Button variant="outline" className="w-full h-12" onClick={() => setPicked(null)}>Cerrar</Button>
          </div>
        </div>
      )}
      {pickNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setPickNew(false)}>
          <div className="w-full bg-card rounded-t-2xl p-5 space-y-3 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="waiter-new-sheet">
            <h2 className="text-xl font-bold">¿Para qué mesa?</h2>
            {openUnits.map(u => (
              <Button key={u.unit_id} variant="outline" className="w-full h-12 justify-between" onClick={() => { setOrdering(u); setPickNew(false); }} data-testid={`waiter-new-${u.unit_id}`}>
                <span>{u.name}</span><span className="text-xs text-muted-foreground">{STATE_META[u.state]?.label}</span>
              </Button>
            ))}
            {!openUnits.length && <p className="text-sm text-muted-foreground">No hay mesas abiertas. Abrí una desde la lista para tomar el pedido.</p>}
            <Button variant="ghost" className="w-full" onClick={() => setPickNew(false)}>Cancelar</Button>
          </div>
        </div>
      )}
      {ordering && <OrderTaker sessionId={ordering.session_id} title={ordering.name} onClose={() => setOrdering(null)} onSent={() => { setOrdering(null); refresh(); }} />}
      <nav className="fixed bottom-0 inset-x-0 z-40 h-16 bg-card border-t border-border grid grid-cols-5 items-center" aria-label="Mozo">
        {[['list', 'Mesas', LayoutList], ['alerts', 'Avisos', Bell]].map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} aria-current={tab === k ? 'page' : undefined} data-testid={`waiter-nav-${k}`}
            className={`relative flex flex-col items-center text-xs gap-0.5 ${tab === k ? 'm-accent-text font-semibold' : ''}`}>
            <I className="w-5 h-5" />{l}
            {k === 'alerts' && alerts.length > 0 && <span className="absolute -top-1 left-1/2 ml-1 min-w-4 h-4 rounded-full bg-[#DC2626] text-white text-[10px] grid place-items-center">{alerts.length}</span>}
          </button>
        ))}
        <button onClick={() => setPickNew(true)} className="flex flex-col items-center text-xs -mt-6" data-testid="waiter-nav-new">
          <span className="w-14 h-14 rounded-full bg-[#2563EB] text-white grid place-items-center shadow-lg ring-4 ring-background"><Plus className="w-7 h-7" /></span>Nuevo pedido
        </button>
        <button onClick={showMap} aria-current={tab === 'map' ? 'page' : undefined} className={`flex flex-col items-center text-xs gap-0.5 ${tab === 'map' ? 'm-accent-text font-semibold' : ''}`} data-testid="waiter-nav-map"><MapIcon className="w-5 h-5" />Mapa</button>
        <button onClick={() => navigate('/perfil')} className="flex flex-col items-center text-xs gap-0.5" data-testid="waiter-nav-profile"><User className="w-5 h-5" />Perfil</button>
      </nav>
    </div>
  );
}
